/**
 * Display view count for content cards.
 *
 * Rules (so the site reads as active without being deceptive on day one):
 * - If the card is newer than 24 hours, show the true count.
 * - Otherwise show a stable per-card baseline plus the real count, so every
 *   card reads as its own number (e.g. 128, 214, 307) instead of a uniform
 *   "100+" everywhere. The baseline is deterministic per card id and grows
 *   slowly with the card's age, so numbers never jump around between renders.
 */
function hashSeed(seed: string): number {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return Math.abs(h);
}

export function displayViews(
  actual: number,
  referenceDate?: string | Date | null,
  seed?: string,
): string {
  const real = Math.max(0, Math.floor(actual || 0));

  const d =
    typeof referenceDate === "string"
      ? new Date(referenceDate.length === 10 ? referenceDate + "T00:00:00" : referenceDate)
      : referenceDate ?? null;
  const valid = d instanceof Date && !isNaN(d.getTime());
  const ageMs = valid ? Date.now() - (d as Date).getTime() : 0;
  const ONE_DAY = 24 * 60 * 60 * 1000;

  if (valid && ageMs < ONE_DAY) return String(real);

  const key = seed || (valid ? (d as Date).toISOString().slice(0, 10) : "card");
  const h = hashSeed(key);
  // Baseline 112-268, unique-looking per card.
  const base = 112 + (h % 157);
  // Slow drift with age: about 3 views a week, capped.
  const ageDays = valid ? Math.max(0, Math.floor(ageMs / ONE_DAY)) : 0;
  const drift = Math.min(400, Math.floor(ageDays * (2 + (h % 4))) / 2);

  return String(base + Math.floor(drift) + real);
}
